import { EmploymentRecord } from '../types/employment.types';
import { formatDisplayDate } from './dateFormatters';

/**
 * Format employment period for display
 * Handles various combinations of start/end dates
 */
export const formatEmploymentPeriod = (startDate: string, endDate: string, t?: (key: string) => string): string => {
  const hasStartDate = startDate && startDate.trim() !== '';
  
  // If no start date, hide the entire date line
  if (!hasStartDate) {
    return '';
  }
  
  // If start date exists, always show "start - end" format
  const start = formatDisplayDate(startDate, t);
  
  // Handle end date: if empty/null, treat as "Present"
  const end = (endDate && endDate.trim() !== '') ? formatDisplayDate(endDate, t) : t('operabase.general.FN_PRESENT');
  
  return `${start} - ${end}`;
};

/**
 * Build employment title for display
 */
export const buildEmploymentTitle = (employment: EmploymentRecord, t?: (key: string) => string): string => {
  return employment.jobTitle || t('operabase.general.FN_EMPLOYEE');
};

/**
 * Build employment subtitle with period and additional details
 * Used for edit mode cards
 */
export const buildEmploymentSubtitle = (employment: EmploymentRecord, t?: (key: string) => string): string => {
  const parts = [];
  
  // Add employment period only if it exists
  const employmentPeriod = formatEmploymentPeriod(employment.startDate, employment.endDate, t);
  if (employmentPeriod) {
    parts.push(employmentPeriod);
  }
  
  // Add employment type and department if available
  const details = [];
  if (employment.employmentType?.name) {
    details.push(employment.employmentType.name);
  }
  if (employment.department?.name) {
    details.push(employment.department.name);
  }
  
  if (details.length > 0) {
    parts.push(details.join(' • '));
  }
  
  return parts.join(' • ');
};



/**
 * Build employment badge configuration
 * Returns badge object or undefined if no badge should be shown
 */
export const buildEmploymentBadge = (
  employment: EmploymentRecord, 
  t: (key: string) => string
): { label: string; variant: 'primary' | 'info' | 'performance' } | undefined => {
  // Priority 1: Organization acknowledgment status (most important)
  if (!employment.acknowledged) {
    return {
      label: t(`operabase.general.FN_NOT_CONFIRMED_BY_ORGANIZATION`),
      variant: 'info' as const,
    };
  }
  
  // Priority 2: Current employment status (only if acknowledged)
  if (employment.isCurrentEmployment) {
    return {
      label: t('operabase.general.FN_PRESENT'),
      variant: 'primary' as const,
    };
  }
  
  return undefined;
};
